import React, { useEffect, useState } from 'react';
import { CalendarClock, Save, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../context/AuthContext';

const WEEKDAYS = [
  { code: 'Mon', label: 'Mon' },
  { code: 'Tue', label: 'Tue' },
  { code: 'Wed', label: 'Wed' },
  { code: 'Thu', label: 'Thu' },
  { code: 'Fri', label: 'Fri' },
  { code: 'Sat', label: 'Sat' },
  { code: 'Sun', label: 'Sun' },
];

// The backend uses "HH:MM AM/PM" strings. A regular <input type="time"> ships
// 24-hour "HH:MM" — we convert both directions so the field stays HTML-native
// while the API contract stays what the mobile app already sends.
function apiTimeTo24h(v) {
  const m = /^\s*(\d{1,2}):(\d{2})\s*(AM|PM)\s*$/i.exec(v || '');
  if (!m) return '09:00';
  let hh = parseInt(m[1], 10);
  const mm = m[2];
  const ap = m[3].toUpperCase();
  if (ap === 'AM') hh = hh === 12 ? 0 : hh;
  else hh = hh === 12 ? 12 : hh + 12;
  return `${String(hh).padStart(2, '0')}:${mm}`;
}
function h24ToApiTime(v) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v || '');
  if (!m) return '09:00 AM';
  let hh = parseInt(m[1], 10);
  const mm = m[2];
  const ap = hh >= 12 ? 'PM' : 'AM';
  const disp = hh % 12 || 12;
  return `${String(disp).padStart(2, '0')}:${mm} ${ap}`;
}

const DEFAULT_FORM = {
  days_off: [],
  blocked_dates: [],
  window_start: '09:00 AM',
  window_end: '06:00 PM',
  duration_min: 30,
};

export default function CrmSettingsBooking() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [newBlocked, setNewBlocked] = useState('');

  useEffect(() => {
    api.get('/booking-settings')
      .then((r) => setForm({ ...DEFAULT_FORM, ...r.data }))
      .catch(() => toast({ title: "Couldn't load booking settings", variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [toast]);

  const toggleDay = (code) => {
    setForm((f) => {
      const has = f.days_off.includes(code);
      return { ...f, days_off: has ? f.days_off.filter((d) => d !== code) : [...f.days_off, code] };
    });
  };

  const addBlockedDate = () => {
    const v = newBlocked.trim();
    if (!v || form.blocked_dates.includes(v)) { setNewBlocked(''); return; }
    setForm((f) => ({ ...f, blocked_dates: [...f.blocked_dates, v].sort() }));
    setNewBlocked('');
  };
  const removeBlockedDate = (d) => {
    setForm((f) => ({ ...f, blocked_dates: f.blocked_dates.filter((x) => x !== d) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/booking-settings', form);
      setForm({ ...DEFAULT_FORM, ...data });
      toast({ title: 'Booking window saved',
              description: 'The AI will honor these limits on the next call.' });
    } catch (err) {
      toast({ title: 'Save failed',
              description: err?.response?.data?.detail || 'Try again.',
              variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-brand-green/70 p-4">Loading booking settings…</div>;
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="booking-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-brand-green inline-flex items-center gap-2">
          <CalendarClock className="w-6 h-6" /> Booking window
        </h1>
        <p className="text-brand-green/70 text-sm mt-1">
          Tell the AI when it can and can't book showings. These limits apply to every call —
          the AI will refuse a Tuesday if you've blocked Tuesdays, and it'll offer the next
          allowed slot to the caller instead of hanging up.
        </p>
      </div>

      <Card className="p-6 space-y-6">
        {/* Days off */}
        <div>
          <Label className="mb-2 block">Days I'm off</Label>
          <p className="text-xs text-brand-green/60 mb-3">Tap to block a weekday. The AI won't book showings on these days.</p>
          <div className="flex flex-wrap gap-2" data-testid="booking-days-off">
            {WEEKDAYS.map((w) => {
              const on = form.days_off.includes(w.code);
              return (
                <button key={w.code} type="button"
                        onClick={() => toggleDay(w.code)}
                        data-testid={`booking-day-${w.code}`}
                        aria-pressed={on}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          on
                            ? 'bg-brand-green text-brand-cream'
                            : 'bg-brand-cream text-brand-green hover:bg-brand-cream/80'
                        }`}>
                  {w.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Blocked dates */}
        <div>
          <Label className="mb-2 block">Specific dates to block</Label>
          <p className="text-xs text-brand-green/60 mb-3">Holidays, vacation days — anything the AI shouldn't book on.</p>
          <div className="flex gap-2 mb-3">
            <Input type="date" value={newBlocked}
                   onChange={(e) => setNewBlocked(e.target.value)}
                   data-testid="booking-new-blocked-date"
                   className="max-w-xs" />
            <Button type="button" variant="outline" onClick={addBlockedDate}
                    data-testid="booking-add-blocked-date">
              Add
            </Button>
          </div>
          {form.blocked_dates.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="booking-blocked-dates">
              {form.blocked_dates.map((d) => (
                <span key={d} data-testid={`booking-blocked-pill-${d}`}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-cream text-brand-green text-sm">
                  {d}
                  <button type="button" onClick={() => removeBlockedDate(d)}
                          data-testid={`booking-blocked-remove-${d}`}
                          className="hover:bg-red-100 rounded-full p-0.5">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Hours */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="window-start">Earliest booking time</Label>
            <Input id="window-start" type="time"
                   value={apiTimeTo24h(form.window_start)}
                   onChange={(e) => setForm({ ...form, window_start: h24ToApiTime(e.target.value) })}
                   data-testid="booking-window-start" />
            <p className="text-xs text-brand-green/50 mt-1">Currently: {form.window_start}</p>
          </div>
          <div>
            <Label htmlFor="window-end">Latest booking time</Label>
            <Input id="window-end" type="time"
                   value={apiTimeTo24h(form.window_end)}
                   onChange={(e) => setForm({ ...form, window_end: h24ToApiTime(e.target.value) })}
                   data-testid="booking-window-end" />
            <p className="text-xs text-brand-green/50 mt-1">Currently: {form.window_end}</p>
          </div>
        </div>

        {/* Duration */}
        <div>
          <Label htmlFor="duration">Default showing length (minutes)</Label>
          <Input id="duration" type="number" min={5} max={240} step={5}
                 value={form.duration_min}
                 onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) || 30 })}
                 data-testid="booking-duration"
                 className="max-w-xs" />
        </div>

        <div className="pt-2">
          <Button onClick={save} disabled={saving}
                  data-testid="booking-save"
                  className="bg-brand-green text-brand-cream hover:bg-brand-green/90 inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save booking window'}
          </Button>
        </div>
      </Card>

      <AutomationCard />
    </div>
  );
}

// ---------- Google Calendar automation (auto-book + push confirm) ----------
function AutomationCard() {
  const { toast } = useToast();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [settings, setSettings] = React.useState({
    appointment_auto_book: true,
    appointment_push_confirm: false,
  });

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/settings/calendar-automation');
        setSettings(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async (patch) => {
    setSaving(true);
    const optimistic = { ...settings, ...patch };
    setSettings(optimistic);
    try {
      const { data } = await api.patch('/settings/calendar-automation', patch);
      setSettings(data);
      toast({ title: 'Booking automation saved' });
    } catch (err) {
      setSettings(settings);
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return null;
  const pushOn = settings.appointment_push_confirm;

  return (
    <Card className="p-6 space-y-4" data-testid="calendar-automation-card">
      <div className="flex items-start gap-3">
        <CalendarClock className="w-5 h-5 text-brand-gold mt-1" />
        <div>
          <div className="font-serif text-lg text-brand-green">Google Calendar automation</div>
          <p className="text-xs text-brand-green/60 mt-1">
            When the AI finds a clear slot on your calendar, decide whether it books straight away
            or waits for you to confirm from your phone.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-brand-green/10 p-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Label className="text-brand-green">Book appointments automatically</Label>
          <p className="text-[11px] text-brand-green/60 mt-1">
            AI locks the slot in the moment it agrees a time with the caller.
            Turn off if you want every booking to wait for your confirmation.
          </p>
        </div>
        <Switch
          checked={settings.appointment_auto_book}
          disabled={saving}
          onCheckedChange={(v) => save({ appointment_auto_book: v })}
          data-testid="automation-auto-book"
        />
      </div>

      <div className="rounded-lg border border-brand-green/10 p-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Label className="text-brand-green">Send push notification before booking</Label>
          <p className="text-[11px] text-brand-green/60 mt-1">
            The AI tells the caller the calendar looks clear, then sends you a push on the mobile app.
            Tap <b>Confirm</b> → the client gets a text confirmation. Tap <b>Reschedule</b> → the appointment is
            canceled and you call the client back yourself.
          </p>
        </div>
        <Switch
          checked={pushOn}
          disabled={saving}
          onCheckedChange={(v) => save({ appointment_push_confirm: v })}
          data-testid="automation-push-confirm"
        />
      </div>

      {pushOn && (
        <div className="rounded-lg bg-brand-cream p-3 text-xs text-brand-green/80" data-testid="automation-push-hint">
          Confirmations happen in the mobile app. Make sure notifications are enabled on your device — you'll get a
          push the moment the AI finds a match.
        </div>
      )}
    </Card>
  );
}
